import type { AgentResponseMessage } from "@/features/ai-agent/types/agent";

import { pluralize } from "@/utils/string";

/** "2 messages, 1 attachment" — the draft header's line. */
export const summarizeBubbles = (bubbles: AgentResponseMessage[]): string => {
  const media = bubbles.filter((b) => b.mediaUrl).length;
  const text = bubbles.length - media;
  const parts = [`${text} ${pluralize(text, "message")}`];

  if (media) parts.push(`${media} ${pluralize(media, "attachment")}`);

  return parts.join(", ");
};

/** Bubbles as one editable block of text, media dropped: what a reviewer sees
 *  in the "how should it have read?" box. */
export const bubblesText = (bubbles: AgentResponseMessage[]): string =>
  bubbles
    .filter((bubble) => !bubble.mediaUrl)
    .map((bubble) => bubble.text)
    .join("\n\n");

/** The composer's text back into bubbles, the inverse of `bubblesText`: a
 *  blank line is a message boundary, and the draft's media follow as they
 *  were. */
export const bubblesFromText = (
  text: string,
  media: AgentResponseMessage[],
): AgentResponseMessage[] => [
  ...text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => ({ type: "text" as const, text: block })),
  ...media.filter((bubble) => bubble.mediaUrl),
];

/** The "how should it have read?" box as a request body fragment. Absent when the reviewer left it
 *  alone, so saving a verdict never wipes a suggestion made earlier. */
export const suggestedMessagesBody = (text: string | undefined) =>
  text?.trim()
    ? { suggestedMessages: [{ type: "text", text: text.trim() }] }
    : {};
